import cn from 'classnames';
import React from 'react';
import { shallowEqual } from 'react-redux';

import getVisiblePersonalizationFields from 'editor/src/store/design/selector/getVisiblePersonalizationFields';
import { ConditionGroup, ConditionChild } from 'editor/src/store/design/types';
import { useSelector } from 'editor/src/store/hooks';

import PersonalizationFieldLayout from 'editor/src/component/PersonalizationFieldLayout';

import PersonalizationCondition from './PersonalizationCondition';
import PersonalizationImage from './PersonalizationImage';
import PersonalizationShape from './PersonalizationShape';
import PersonalizationText from './PersonalizationText';

import styles from './index.module.scss';

interface Props {
  childrenKey?: string;
  spreadIndex: number;
  conditionGroup: ConditionGroup;
  elements: ConditionChild[];
  personalizationElementClassName?: string;
  /** Forwarded to nested conditions so a borrowed card's whole chain of
   *  picklists stays picklist-only. See PersonalizationCondition. */
  optionsOnly?: boolean;
}

function PersonalizationElements({
  childrenKey = 'top',
  spreadIndex,
  conditionGroup,
  elements,
  personalizationElementClassName,
  optionsOnly,
}: Props) {
  const visibleElements = useSelector(
    (state) => getVisiblePersonalizationFields(state.design.designData, elements, spreadIndex),
    shallowEqual,
  );
  return (
    <div
      className={cn(styles.PersonalizationElements, {
        [styles.notEmpty]: !!elements.length,
      })}
    >
      <PersonalizationFieldLayout
        conditionGroup={conditionGroup}
        elements={visibleElements}
        childrenKey={childrenKey}
        renderElement={(element) => {
          const key = element.type + element.id;
          switch (element.type) {
            case 'text':
              return (
                <PersonalizationText key={key} elementId={element.id} className={personalizationElementClassName} />
              );
            case 'image':
            case 'addon':
              return (
                <PersonalizationImage key={key} elementId={element.id} className={personalizationElementClassName} />
              );
            case 'line':
            case 'rectangle':
              return (
                <PersonalizationShape key={key} elementId={element.id} className={personalizationElementClassName} />
              );
            case 'condition':
              return (
                <PersonalizationCondition
                  key={key}
                  spreadIndex={spreadIndex}
                  conditionGroup={conditionGroup}
                  condition={conditionGroup.conditions[element.id]}
                  isBuilding={false}
                  textOnly={false}
                  optionsOnly={optionsOnly}
                />
              );
            default:
              return null;
          }
        }}
      />
    </div>
  );
}

export default React.memo(PersonalizationElements);
